<template>
    <b-modal
        :visible="value"
        size="lg"
        centered
        hide-footer
        hide-header
        no-close-on-backdrop
        modal-class="apt-modal"
        content-class="apt-modal-content"
        body-class="p-0"
        @change="onVisibleChange"
    >
        <!-- gradient header -->
        <div class="apt-header" :style="{'--apt-accent': currentTypeColor}">
            <div class="apt-header-icon">
                <feather-icon :icon="currentModeIcon" size="22"/>
            </div>
            <div class="apt-header-text">
                <h4 class="mb-0">{{ isCreate ? msg('New appointment') : msg('Edit appointment') }}</h4>
                <small v-if="!isCreate && localAppointment">#{{ localAppointment.id }} · {{ msg(currentTypeLabel) }}</small>
                <small v-else>{{ msg(currentTypeLabel) }} · {{ durationMinutes }} {{ msg('min') }}</small>
            </div>
            <b-button variant="flat-light" class="apt-close btn-icon" @click="close">
                <feather-icon icon="XIcon" size="18"/>
            </b-button>
        </div>

        <div class="apt-body">
            <validation-observer ref="observer">
                <!-- Title -->
                <div class="mb-1">
                    <label class="apt-label">{{ msg('Title') }} <span class="text-danger">*</span></label>
                    <validation-provider #default="{ errors }" :name="msg('Title')" rules="required">
                        <b-form-input v-model="form.title" :placeholder="msg('e.g. First consultation with Max')"
                                      :state="errors.length ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                </div>

                <!-- Mode pills -->
                <div class="mb-1">
                    <label class="apt-label">{{ msg('Appointment mode') }}</label>
                    <div class="apt-mode-group">
                        <button v-for="m in modes" :key="m.value" type="button"
                                class="apt-mode-pill" :class="{active: form.mode === m.value}"
                                @click="form.mode = m.value">
                            <feather-icon :icon="m.icon" size="15" class="mr-25"/>{{ msg(m.label) }}
                        </button>
                    </div>
                </div>

                <!-- Type cards -->
                <div class="mb-1">
                    <label class="apt-label">{{ msg('Appointment type') }}</label>
                    <div class="apt-type-grid">
                        <button v-for="t in types" :key="t.value" type="button"
                                class="apt-type-card" :class="{active: form.type === t.value}"
                                :style="{'--apt-type-color': t.color}"
                                @click="selectType(t)">
                            <span class="apt-type-dot"/>
                            <span class="apt-type-name">{{ msg(t.label) }}</span>
                            <span class="apt-type-duration">{{ t.duration }} {{ msg('min') }}</span>
                        </button>
                    </div>
                </div>

                <!-- Date & time -->
                <b-row>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('Start') }} <span class="text-danger">*</span></label>
                        <validation-provider #default="{ errors }" :name="msg('Start')" rules="required">
                            <flat-pickr v-model="form.start" class="form-control"
                                        :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true, minuteIncrement: 5 }"/>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('End') }}</label>
                        <flat-pickr v-model="form.end" class="form-control"
                                    :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true, minuteIncrement: 5 }"/>
                        <small v-if="durationMinutes > 0" class="apt-duration-hint">
                            <feather-icon icon="ClockIcon" size="12"/> {{ durationText }}
                        </small>
                        <small v-else-if="form.start && form.end" class="text-danger">
                            {{ msg('End time must be after start time') }}
                        </small>
                    </b-col>
                </b-row>

                <!-- Owner & attendees -->
                <b-row>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('Assign to user') }}</label>
                        <v-select v-model="form.ownerUserId" :options="userOptions" :reduce="o => o.id"
                                  label="name" append-to-body :clearable="false"
                                  :placeholder="msg('Select user')"/>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('Additional participants') }}</label>
                        <v-select v-model="form.attendeeUserIds" :options="userOptions" :reduce="o => o.id"
                                  label="name" multiple append-to-body
                                  :placeholder="msg('Invite other users')"/>
                    </b-col>
                </b-row>

                <!-- Mode-dependent detail + branch -->
                <b-row>
                    <b-col cols="12" md="6" class="mb-1">
                        <template v-if="form.mode === 'PHYSICAL'">
                            <label class="apt-label">{{ msg('Location') }}</label>
                            <b-form-input v-model="form.location" :placeholder="msg('Address or meeting room')"/>
                        </template>
                        <template v-else-if="form.mode === 'PHONE'">
                            <label class="apt-label">{{ msg('Phone number') }}</label>
                            <b-form-input v-model="form.phoneNumber" :placeholder="msg('Number to call')"/>
                        </template>
                        <template v-else>
                            <label class="apt-label">{{ msg('Meeting link') }}</label>
                            <b-form-input v-model="form.meetingLink" placeholder="https://meet..."/>
                        </template>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('Branch') }}</label>
                        <v-select v-model="form.buId" :options="branchOptions" :reduce="o => o.id"
                                  label="name" append-to-body :placeholder="msg('Select branch')"/>
                    </b-col>
                </b-row>

                <!-- External contact -->
                <b-row>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('Contact name') }} <small class="text-muted">({{ msg('optional') }})</small></label>
                        <b-form-input v-model="form.contactName" :placeholder="msg('Lead / client name')"/>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="apt-label">{{ msg('Contact info') }} <small class="text-muted">({{ msg('optional') }})</small></label>
                        <b-form-input v-model="form.contactInfo" :placeholder="msg('Email or phone')"/>
                    </b-col>
                </b-row>

                <!-- Description -->
                <div class="mb-1">
                    <label class="apt-label">{{ msg('Notes') }}</label>
                    <b-form-textarea v-model="form.description" rows="3" max-rows="8"
                                     :placeholder="msg('Agenda, preparation, notes…')"/>
                </div>

                <!-- Status (edit mode) -->
                <div v-if="!isCreate" class="mb-1">
                    <label class="apt-label">{{ msg('Status') }}</label>
                    <div class="apt-mode-group">
                        <button v-for="s in statuses" :key="s.value" type="button"
                                class="apt-mode-pill apt-status-pill" :class="[{active: form.status === s.value}, 'apt-status--' + s.value.toLowerCase()]"
                                @click="form.status = s.value">
                            {{ msg(s.label) }}
                        </button>
                    </div>
                </div>

                <!-- meta line (edit mode) -->
                <div v-if="!isCreate && localAppointment" class="apt-meta-line">
                    <span>{{ msg('Created by') }}: <strong>{{ localAppointment.createdByName || ('#' + localAppointment.createdBy) }}</strong></span>
                    <span v-if="localAppointment.ownerName">{{ msg('Assigned to') }}: <strong>{{ localAppointment.ownerName }}</strong></span>
                </div>

                <!-- actions -->
                <div class="d-flex align-items-center mt-1">
                    <b-button v-if="!isCreate" variant="outline-danger" :disabled="saving" @click="confirmDelete">
                        <feather-icon icon="Trash2Icon" size="14" class="mr-25"/>{{ msg('Delete') }}
                    </b-button>
                    <div class="ml-auto">
                        <b-button variant="outline-secondary" class="mr-1" @click="close">{{ msg('Cancel') }}</b-button>
                        <b-button variant="primary" :disabled="saving || durationMinutes <= 0" @click="submit">
                            <b-spinner v-if="saving" small class="mr-50"/>
                            {{ isCreate ? msg('Create appointment') : msg('Save') }}
                        </b-button>
                    </div>
                </div>
            </validation-observer>
        </div>
    </b-modal>
</template>

<script>
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import {
    APPOINTMENT_MODES, APPOINTMENT_TYPES, APPOINTMENT_STATUSES,
    appointmentType, appointmentMode,
} from '@/model/appointment/appointmentEnums'

export default {
    name: 'AppointmentModal',
    components: {ValidationObserver, ValidationProvider, vSelect, flatPickr},
    props: {
        value: {type: Boolean, default: false},
        // null/empty → create mode; otherwise edit mode for this appointment
        appointment: {type: Object, default: null},
        // prefill values for create mode (e.g. from calendar date click or leads page)
        defaults: {type: Object, default: null},
    },
    data() {
        return {
            localAppointment: null,
            saving: false,
            populating: false,
            form: this.emptyForm(),
            modes: APPOINTMENT_MODES,
            types: APPOINTMENT_TYPES,
            statuses: APPOINTMENT_STATUSES,
        }
    },
    computed: {
        isCreate() {
            return !(this.appointment && this.appointment.id);
        },
        myId() {
            const p = this.$store.getters['user/myProfile'];
            return p ? p.id : null;
        },
        userOptions() {
            const users = this.$store.getters['appointment/getAssignableUsers'] || [];
            if (this.myId != null && !users.some((u) => u.id === this.myId)) {
                const myName = this.$store.getters['user/myName'];
                return [{id: this.myId, name: myName || ('#' + this.myId)}, ...users];
            }
            return users;
        },
        branchOptions() {
            const branches = this.$store.getters['data/getAllBranches'] || {};
            return Object.values(branches).map((b) => ({id: b.id, name: b.name}));
        },
        currentTypeColor() {
            return appointmentType(this.form.type).color;
        },
        currentTypeLabel() {
            return appointmentType(this.form.type).label;
        },
        currentModeIcon() {
            return appointmentMode(this.form.mode).icon;
        },
        durationMinutes() {
            const s = this.parseDate(this.form.start);
            const e = this.parseDate(this.form.end);
            if (!s || !e) return 0;
            return Math.round((e.getTime() - s.getTime()) / 60000);
        },
        durationText() {
            const m = this.durationMinutes;
            if (m < 60) return m + ' ' + this.msg('min');
            const h = Math.floor(m / 60);
            const rest = m % 60;
            return h + ' ' + this.msg('h') + (rest ? ' ' + rest + ' ' + this.msg('min') : '');
        },
    },
    watch: {
        value(open) {
            if (open) this.onOpen();
        },
        // moving the start keeps the duration (shifts the end along)
        'form.start'(newVal, oldVal) {
            if (this.populating) return; // form was replaced wholesale, not user input
            const oldStart = this.parseDate(oldVal);
            const newStart = this.parseDate(newVal);
            const end = this.parseDate(this.form.end);
            if (!oldStart || !newStart || !end) return;
            const duration = end.getTime() - oldStart.getTime();
            if (duration > 0) {
                this.form.end = this.formatDate(new Date(newStart.getTime() + duration));
            }
        },
    },
    methods: {
        emptyForm() {
            return {
                title: '',
                description: '',
                mode: 'PHYSICAL',
                type: 'MEETING',
                status: 'SCHEDULED',
                start: '',
                end: '',
                location: '',
                phoneNumber: '',
                meetingLink: '',
                ownerUserId: null,
                attendeeUserIds: [],
                contactName: '',
                contactInfo: '',
                clientId: null,
                leadId: null,
                buId: null,
                color: null,
            };
        },
        onOpen() {
            this.populating = true;
            this.$nextTick(() => {
                this.populating = false;
            });
            if (!(this.$store.getters['appointment/getAssignableUsers'] || []).length) {
                this.$store.dispatch('appointment/loadAssignableUsers');
            }
            if (this.isCreate) {
                this.localAppointment = null;
                this.form = this.emptyForm();
                this.form.ownerUserId = this.myId;
                const selected = this.$store.getters['data/getSelectedBranchesIds'] || [];
                if (selected.length) this.form.buId = selected[0];
                if (this.defaults) {
                    Object.keys(this.defaults).forEach((k) => {
                        if (this.defaults[k] !== undefined && this.defaults[k] !== null) {
                            this.$set(this.form, k, this.defaults[k]);
                        }
                    });
                }
                if (!this.form.start) this.form.start = this.formatDate(this.roundToNextHalfHour(new Date()));
                if (!this.form.end) this.applyTypeDuration();
            } else {
                this.localAppointment = JSON.parse(JSON.stringify(this.appointment));
                this.populateForm(this.localAppointment);
            }
        },
        populateForm(a) {
            this.form = {
                title: a.title || '',
                description: a.description || '',
                mode: a.mode || 'PHYSICAL',
                type: a.type || 'MEETING',
                status: a.status || 'SCHEDULED',
                start: a.start || '',
                end: a.end || '',
                location: a.location || '',
                phoneNumber: a.phoneNumber || '',
                meetingLink: a.meetingLink || '',
                ownerUserId: a.ownerUserId,
                attendeeUserIds: Array.isArray(a.attendeeUserIds) ? [...a.attendeeUserIds]
                    : (a.attendeeUserIds ? Object.values(a.attendeeUserIds) : []),
                contactName: a.contactName || '',
                contactInfo: a.contactInfo || '',
                clientId: a.clientId != null ? a.clientId : null,
                leadId: a.leadId || null,
                buId: a.buId != null ? a.buId : null,
                color: a.color || null,
            };
        },
        selectType(t) {
            this.form.type = t.value;
            this.applyTypeDuration();
        },
        // end = start + default duration of the selected type
        applyTypeDuration() {
            const s = this.parseDate(this.form.start);
            if (!s) return;
            const dur = appointmentType(this.form.type).duration;
            this.form.end = this.formatDate(new Date(s.getTime() + dur * 60000));
        },
        onVisibleChange(v) {
            this.$emit('input', v);
        },
        close() {
            this.$emit('input', false);
        },
        async submit() {
            const ok = await this.$refs.observer.validate();
            if (!ok) return;
            if (this.durationMinutes <= 0) return;
            this.saving = true;
            try {
                const payload = {...this.form};
                if (this.isCreate) {
                    await this.$store.dispatch('appointment/create', payload);
                    this.notifySuccess(this.msg('Appointment created'));
                } else {
                    await this.$store.dispatch('appointment/update', {id: this.localAppointment.id, appointment: payload});
                    this.notifySuccess(this.msg('Appointment updated'));
                }
                this.$emit('saved');
                this.close();
            } catch (e) { /* error notification handled by the store */
            } finally {
                this.saving = false;
            }
        },
        async confirmDelete() {
            const yes = await this.$bvModal.msgBoxConfirm(this.msg('Delete this appointment?'), {
                title: this.msg('Delete'),
                okVariant: 'danger',
                okTitle: this.msg('Delete'),
                cancelTitle: this.msg('Cancel'),
                centered: true,
            });
            if (!yes) return;
            this.saving = true;
            try {
                await this.$store.dispatch('appointment/remove', this.localAppointment.id);
                this.notifySuccess(this.msg('Appointment deleted'));
                this.$emit('deleted');
                this.close();
            } catch (e) { /* handled by store */
            } finally {
                this.saving = false;
            }
        },
        notifySuccess(message) {
            this.$store.dispatch('notifications/success', {
                title: this.msg('Calendar'), message,
            }, {root: true});
        },
        // "yyyy-MM-dd HH:mm" → Date
        parseDate(str) {
            if (!str) return null;
            const d = new Date(String(str).replace(' ', 'T'));
            return isNaN(d.getTime()) ? null : d;
        },
        // Date → "yyyy-MM-dd HH:mm"
        formatDate(d) {
            const p = (n) => String(n).padStart(2, '0');
            return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
                + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
        },
        roundToNextHalfHour(d) {
            const out = new Date(d.getTime());
            out.setSeconds(0, 0);
            const m = out.getMinutes();
            out.setMinutes(m <= 30 ? 30 : 60);
            return out;
        },
    },
}
</script>

<style lang="scss" scoped>
/* flatpickr styles are loaded globally (plugins/ui-core.js → vue-flatpicker.scss) */
.apt-label {
    font-size: .8rem;
    font-weight: 600;
    margin-bottom: .25rem;
    display: block;
    color: #5e5873;
}

.apt-duration-hint {
    display: inline-flex;
    align-items: center;
    gap: .25rem;
    margin-top: .25rem;
    color: #7367f0;
    font-weight: 600;
}

.apt-meta-line {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: .5rem;
    font-size: .82rem;
    color: #6e6b7b;
    padding: .25rem 0;
}
</style>

<style lang="scss">
/* Unscoped: the b-modal content is portaled to <body> (keyed to content-class). */
.apt-modal-content {
    border-radius: .8rem;
    overflow: hidden;
}

.apt-modal-content .apt-header {
    display: flex;
    align-items: center;
    gap: .9rem;
    padding: 1.1rem 1.4rem;
    background: linear-gradient(120deg, var(--apt-accent, #7367f0), #4b4b9b);
    color: #fff;

    h4 {
        color: #fff;
        font-weight: 700;
    }

    small {
        opacity: .85;
    }
}

.apt-modal-content .apt-header-icon {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    border-radius: .65rem;
    background: rgba(255, 255, 255, .18);
    display: flex;
    align-items: center;
    justify-content: center;
}

.apt-modal-content .apt-close {
    margin-left: auto;
    color: #fff !important;
}

.apt-modal-content .apt-body {
    padding: 1.25rem 1.4rem 1.4rem;
}

/* mode pills */
.apt-modal-content .apt-mode-group {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
}

.apt-modal-content .apt-mode-pill {
    display: inline-flex;
    align-items: center;
    padding: .45rem .9rem;
    border-radius: 2rem;
    border: 1px solid #d8d6de;
    background: transparent;
    color: #6e6b7b;
    font-weight: 600;
    font-size: .85rem;
    transition: all .15s ease;
    cursor: pointer;

    &:hover {
        border-color: #7367f0;
        color: #7367f0;
    }

    &.active {
        background: #7367f0;
        border-color: #7367f0;
        color: #fff;
        box-shadow: 0 4px 12px rgba(115, 103, 240, .35);
    }
}

/* status pill colors */
.apt-modal-content .apt-status-pill.active {
    box-shadow: none;

    &.apt-status--scheduled { background: #7367f0; border-color: #7367f0; }
    &.apt-status--completed { background: #28c76f; border-color: #28c76f; }
    &.apt-status--cancelled { background: #82868b; border-color: #82868b; }
    &.apt-status--no_show { background: #ea5455; border-color: #ea5455; }
}

/* type cards */
.apt-modal-content .apt-type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
    gap: .5rem;
}

.apt-modal-content .apt-type-card {
    display: flex;
    align-items: center;
    gap: .45rem;
    padding: .55rem .7rem;
    border-radius: .55rem;
    border: 1px solid #d8d6de;
    background: transparent;
    color: #5e5873;
    font-size: .8rem;
    font-weight: 600;
    text-align: left;
    transition: all .15s ease;
    cursor: pointer;

    .apt-type-dot {
        width: 10px;
        height: 10px;
        flex: 0 0 10px;
        border-radius: 50%;
        background: var(--apt-type-color);
    }

    .apt-type-name {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .apt-type-duration {
        font-size: .7rem;
        color: #b9b9c3;
        font-weight: 500;
    }

    &:hover {
        border-color: var(--apt-type-color);
    }

    &.active {
        border-color: var(--apt-type-color);
        background: color-mix(in srgb, var(--apt-type-color) 12%, transparent);
        box-shadow: inset 0 0 0 1px var(--apt-type-color);

        .apt-type-duration {
            color: var(--apt-type-color);
        }
    }
}

/* ── dark mode ── */
.dark-layout .apt-modal-content,
.dark-layout .apt-modal-content .modal-body {
    background-color: #283046;
}

.dark-layout .apt-modal-content .apt-label,
.dark-layout .apt-modal-content .apt-type-card {
    color: #d0d2d6;
}

.dark-layout .apt-modal-content .apt-meta-line {
    color: #b4b7bd;
}

.dark-layout .apt-modal-content .apt-mode-pill,
.dark-layout .apt-modal-content .apt-type-card {
    border-color: #3b4253;
}

.dark-layout .apt-modal-content .apt-mode-pill {
    color: #b4b7bd;

    &.active {
        color: #fff;
    }
}
</style>
